import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageProvider";
import { APP_VERSION } from "@/lib/appVersion";

// Internal Privacy Policy + Terms of Use routes (self-hosted — see
// src/pages/Legal/).
const PRIVACY_POLICY_URL = "/privacy";
const TERMS_URL = "/terms";

export interface FooterProps {
  variant?: "minimal" | "full";
  className?: string;
}

const linkClass =
  "transition-colors no-underline hover:underline hover:text-white/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-2 focus:ring-offset-transparent rounded";

const Footer: React.FC<FooterProps> = ({ variant = "full", className = "" }) => {
  const { t } = useLanguage();

  const privacyLink = (
    <Link
      to={PRIVACY_POLICY_URL}
      className={linkClass}
      style={{ color: "rgba(255,255,255,0.5)" }}
    >
      {t("footer.legal.privacy")}
    </Link>
  );

  const termsLink = (
    <Link
      to={TERMS_URL}
      className={linkClass}
      style={{ color: "rgba(255,255,255,0.5)" }}
    >
      {t("footer.legal.terms")}
    </Link>
  );

  const contactLink = (
    <Link to="/privacy-request" className={linkClass} style={{ color: "rgba(255,255,255,0.5)" }}>
      {t("footer.legal.contact")}
    </Link>
  );

  // Public no-login page required by Google Play Data Safety (account
  // deletion URL) — see src/pages/Legal/DataDeletion.tsx.
  const dataDeletionLink = (
    <Link to="/data-deletion" className={linkClass} style={{ color: "rgba(255,255,255,0.5)" }}>
      {t("footer.legal.dataDeletion")}
    </Link>
  );

  if (variant === "minimal") {
    return (
      <footer
        aria-label="Site footer"
        className={`w-full border-t border-white/[0.08] py-6 md:py-8 px-4 md:px-8 mt-auto ${className}`}
      >
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] font-body text-center">
          <p style={{ color: "rgba(255,255,255,0.5)" }}>{t("footer.copyright")}</p>
          <span style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
          {privacyLink}
          <span style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
          {termsLink}
          <span style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
          {dataDeletionLink}
          <span style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
          {contactLink}
          <span style={{ color: "rgba(255,255,255,0.2)" }}>·</span>
          <span style={{ color: "rgba(255,255,255,0.28)" }} title="build">v{APP_VERSION}</span>
        </div>
      </footer>
    );
  }

  return (
    <footer
      aria-label="Site footer"
      className={`w-full border-t border-white/[0.08] py-6 md:py-8 px-4 md:px-8 mt-auto ${className}`}
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
        <div className="flex flex-col gap-1 max-w-xs">
          <p className="font-display text-[13px] font-semibold tracking-wide" style={{ color: "rgba(255,255,255,0.72)" }}>
            VYV
          </p>
          <p className="text-[11px] font-body" style={{ color: "rgba(255,255,255,0.4)" }}>
            {t("footer.tagline")}
          </p>
          <p className="text-[11px] font-body leading-relaxed mt-1" style={{ color: "rgba(255,255,255,0.4)" }}>
            {t("footer.appDescription")}
          </p>
        </div>

        <nav
          aria-label="Legal"
          className="flex flex-col gap-1.5 text-[11px] font-body"
        >
          {privacyLink}
          {termsLink}
          {dataDeletionLink}
          {contactLink}
        </nav>

        <p className="text-[10px] font-body leading-relaxed md:text-right" style={{ color: "rgba(255,255,255,0.35)" }}>
          {t("footer.copyright")}
          <span className="block mt-0.5" style={{ color: "rgba(255,255,255,0.25)" }} title="build">
            v{APP_VERSION}
          </span>
        </p>
      </div>
    </footer>
  );
};

export default Footer;
